import { AlertCircle } from "lucide-react";

import { HealthlinkXsltFrame } from "features/healthlink/components/HealthlinkXsltFrame/HealthlinkXsltFrame";
import { useLabOrderXml } from "features/labOrders/hooks/useLabOrderXml";
import { LAB_ORDER_MESSAGE_XSL } from "features/labOrders/utils/labOrderMessageXsl";

import s from "./LabOrderMessagePreview.module.css";

// The order as HL7, rendered with **Healthlink's own stylesheet** when the backend can
// fetch it — a retrieved order names it outright (LabOrderPrint_GP_2.20.xsl), and it is
// what produces the printable Lab Order Request the GP knows, barcode included (the
// barcode travels inside the message as a base64 image). Ours is the fallback for when
// Healthlink cannot be reached or has nothing for this message type.
//
// One document: the completed order Healthlink assembled. The template we sent is not
// shown here — it carries no tests and no barcode, and rendering it through Healthlink's
// stylesheet produces a broken barcode link (KIN-1542).
export const LabOrderMessagePreview = ({ messageControlId }) => {
  const { data, isLoading } = useLabOrderXml(messageControlId);

  // The completed order only — never the template we sent. Healthlink's stylesheet reads
  // the barcode from an OBX that exists only on a completed order; handed our MSH+PID+PV1
  // template it falls back to an image URL with an empty Number param and renders a broken
  // barcode (KIN-1542). A template has no tests to show either, so there is nothing lost.
  const xml = data?.xmlRetrievedContent;
  const xsl = data?.xslContent || LAB_ORDER_MESSAGE_XSL;

  // Nothing is shown while loading: the frame appears with the document, rather than a
  // spinner standing in for it.
  if (isLoading) return null;

  if (!xml) {
    return (
      <section className={s.wrapper}>
        <div className={s.notice} role="status">
          <AlertCircle size={20} className={s.noticeIcon} aria-hidden />
          <div>
            <p className={s.noticeTitle}>No order preview</p>
            <p className={s.noticeMessage}>
              Healthlink hasn&apos;t returned this order&apos;s message yet. Once
              the order is completed and downloaded, it appears here.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={s.wrapper}>
      <HealthlinkXsltFrame xml={xml} xsl={xsl} />
    </section>
  );
};
